import {
  AggregatedUserEventsPerOrgUnit,
  PrimaryApplication,
  SecondaryApplication,
  TimeseriesUserEventsPerOrgUnit,
  UserEventsPerOrgUnitBase,
} from '@percept/app-components'

import { ChartData } from '@percept/mui/charts'

import { get, identity, some, sortBy } from 'lodash-es'

import { dateFnsOps, getPostgresNormalisedDateLabelsForGranularity, produceKeyedMapping } from '@percept/utils'

import { ReportProviderV2, TimeseriesGranularity } from '@percept/types'

import { TimelineDatum } from 'types'


export type UsageTimeseriesData = {
  regularity?: number | null
  chartData: ChartData<TimelineDatum> | null
}

export type UsageTotalsRow = (
  UsageTimeseriesData & {
    key: string
    unique_users: number | null
    views: number | null
    views_per_day?: number | null
    provider: ReportProviderV2
    primary_application: PrimaryApplication
    secondary_application: SecondaryApplication
    org_unit_name: string
    org_unit_id: string
  }
)

export const getRowKey = (row: UserEventsPerOrgUnitBase): string => (
  [row.org_unit_id, row.provider, row.primary_application, row.secondary_application].join('|')
)

export const getAnalyticsTimeseriesMapping = ({
  data,
  startDate,
  endDate,
  granularity,
  color,
  numDaysInRange,
}: {
  data: TimeseriesUserEventsPerOrgUnit[] | null | undefined
  startDate: string | number | null
  endDate: string | number | null
  granularity: TimeseriesGranularity
  color: string
  numDaysInRange: number | null
}): Record<string, UsageTimeseriesData> => {

  if( !data || !startDate || !endDate ){
    return {}
  }

  const range: [Date, Date] = [new Date(startDate), new Date(endDate)]

  const labels = getPostgresNormalisedDateLabelsForGranularity(range, granularity)

  const endDateCast: ((d: Date) => Date) = (
    granularity === 'DAY' ?
      identity :
      granularity === 'WEEK' ?
        d => dateFnsOps.endOfWeek(d, { weekStartsOn: 1 }) :
        dateFnsOps.endOfMonth
  )

  return data.reduce( (acc, row) => {

    const sparseTimeseriesDataMapping = produceKeyedMapping(row.timeseries, 'start_date')

    const rowKey = getRowKey(row)

    acc[rowKey] = {
      chartData: labels.map( startIsoDate => {
        const startDate = new Date(startIsoDate)
        const matchedDatum = sparseTimeseriesDataMapping[startIsoDate]
        const value = get(matchedDatum, 'total_user_events', 0)
        const start = Math.max(startDate.getTime(), range[0].getTime())
        let end
        if( matchedDatum ){
          end = new Date(matchedDatum.end_date).getTime()
        }else if( granularity === 'DAY' ){
          end = start
        }else{
          end = endDateCast(startDate).getTime()
        }
        // Ensure end date doesn't exceed the range
        end = Math.min(end, range[1].getTime())
        return {
          value,
          label: start,
          start,
          end,
          color,
        }
      }),
      // regularity: (
      //   Math.round(
      //     row.timeseries.map(
      //       d => {
      //         if (d.total_user_events > 0) {
      //           if (d.end_date === d.start_date) {
      //             return 1
      //           } else {
      //             return Math.abs(differenceInDays(new Date(d.end_date), new Date(d.start_date)))
      //           }
      //         }
      //         return 0
      //       }
      //     ).reduce((acc, val) => acc + val, 0) * 100 / numDaysInRange
      //   ) / 100
      // ),
    }

    return acc
  }, {} as Record<string, UsageTimeseriesData>)
}


export const getTableRows = ({
  data,
  timeseriesMapping,
  numDaysInRange,
}: {
  data: AggregatedUserEventsPerOrgUnit[] | undefined
  timeseriesMapping: Record<string, UsageTimeseriesData>
  numDaysInRange: number | null
}): UsageTotalsRow[] => {
  if (!data) {
    return []
  }

  const sortedRows = sortBy(
    data.map( (query_row) => {
      const key = getRowKey(query_row)
      return {
        key,
        org_unit_name: query_row.org_unit_name,
        org_unit_id: query_row.org_unit_id,
        provider: query_row.provider,
        primary_application: query_row.primary_application,
        secondary_application: query_row.secondary_application,
        unique_users: query_row.total_unique_users,
        views: query_row.total_user_events,
        views_per_day: (
          query_row.total_user_events && numDaysInRange ?
            Math.round(100 * query_row.total_user_events / numDaysInRange) / 100 :
            null
        ),
        regularity: get(timeseriesMapping, [key, 'regularity'], null),
        chartData: get(timeseriesMapping, [key, 'chartData'], null),
      }
    }),
    ['org_unit_name', 'secondary_application', 'provider']
  ) as UsageTotalsRow[]

  // Filter null page views where there are non-null events for the given org
  const secondaryAppsByOrgUnitId = sortedRows.reduce( (acc, row) => {
    if( !acc[row.org_unit_id] ){
      acc[row.org_unit_id] = []
    }
    acc[row.org_unit_id].push(row.secondary_application)
    return acc
  }, {} as Record<string, (SecondaryApplication | null)[]>)

  const idsRequiringNullFiltering = Object.entries(secondaryAppsByOrgUnitId).reduce( (acc, [orgUnitId, secondaryApps]) => {
    if( secondaryApps.length && secondaryApps.includes(null) && some(secondaryApps) ){
      acc.push(orgUnitId)
    }
    return acc
  }, [] as string[])

  if( !idsRequiringNullFiltering.length ){
    return sortedRows
  }
  return sortedRows.filter( r => {
    if(
      idsRequiringNullFiltering.includes(r.org_unit_id)
      && !r.secondary_application
    ){
      return false
    }
    return true
  })
}
